// Resolves where a book's bytes come from, abstracting over the library folder
// (preferred) and the IndexedDB fallback. Used by the reader (load a blob) and
// the home page (store downloads, decide which books are readable).

import { getBook, saveBook, storedBookIds, getFileMap, putFileMap } from './db';
import {
  getFolder,
  hasPermission,
  scanFolder,
  readFolderFile,
  writeFolderFile,
  fileNameFor,
  normalizeName,
  type FolderFile,
} from './fsfolder';

function matchFile(title: string, files: FolderFile[]): FolderFile | undefined {
  const want = normalizeName(title);
  if (!want) return undefined;
  const exact = files.find((f) => f.norm === want);
  if (exact) return exact;
  // Looser containment, but only for reasonably long names to avoid false hits.
  if (want.length >= 5) {
    return files.find((f) => f.norm.length >= 5 && (f.norm.includes(want) || want.includes(f.norm)));
  }
  return undefined;
}

/** Get a book's bytes: library folder first (mapping, then fuzzy match), then IndexedDB. */
export async function resolveBookBlob(
  bookId: string,
  title: string,
): Promise<Blob | null> {
  const folder = await getFolder();
  if (folder && (await hasPermission(folder, false))) {
    const mapped = await getFileMap(bookId);
    if (mapped) {
      const blob = await readFolderFile(folder, mapped);
      if (blob) return blob;
    }
    const files = await scanFolder(folder);
    const hit = matchFile(title, files);
    if (hit) {
      const blob = await readFolderFile(folder, hit.name);
      if (blob) {
        putFileMap(bookId, hit.name).catch(() => {});
        return blob;
      }
    }
  }
  const rec = await getBook(bookId);
  return rec?.blob ?? null;
}

/** Save a downloaded book into the folder if configured, else IndexedDB. */
export async function storeDownload(
  bookId: string,
  title: string,
  type: 'PDF' | 'EPUB',
  blob: Blob,
): Promise<'folder' | 'idb'> {
  const folder = await getFolder();
  if (folder && (await hasPermission(folder, true))) {
    const name = fileNameFor(title, type);
    await writeFolderFile(folder, name, blob);
    await putFileMap(bookId, name);
    return 'folder';
  }
  await saveBook(bookId, title, type, blob);
  return 'idb';
}

/** Which of these books are readable now (folder match/mapping or IndexedDB). */
export async function availableBookIds(books: { id: string; title: string }[]): Promise<Set<string>> {
  const out = new Set<string>(await storedBookIds());

  const folder = await getFolder();
  if (folder && (await hasPermission(folder, false))) {
    const files = await scanFolder(folder);
    for (const b of books) {
      if (out.has(b.id)) continue;
      const mapped = await getFileMap(b.id);
      if (mapped) {
        out.add(b.id);
        continue;
      }
      if (matchFile(b.title, files)) out.add(b.id);
    }
  }
  return out;
}
